import { InMemoryCache } from '@apollo/client'
import { relayStylePagination } from '@apollo/client/utilities'
import { filterEmptyPurchaseDetails } from './helpers/filterEmptyPurchaseDetails/filterEmptyPurchaseDetails'
import { mergeAcceptIncoming } from './helpers/mergeAcceptIncoming/mergeAcceptIncoming'
import { mergePreservePopulatedString } from './helpers/mergePreservePopulatedString/mergePreservePopulatedString'
import { readManualOrderDate } from './helpers/readManualOrderDate/readManualOrderDate'
import autoMergeTypes from 'gql/auto-merge-types.json'

// Arrays of data, where incoming array should completely substitute existed one.
// Those are queries that we're fetching without parameters.
const replaceableArrayQueryNames = [
  'businesses',
  'plaidConnections',
  'finicityConnections',
  'accounts',
  'invoicesFilteringOptions',
]

const replaceableArrays = replaceableArrayQueryNames.reduce<Record<string, { merge: false }>>((all, queryName) => {
  all[queryName] = {
    merge: false,
  }
  return all
}, {})

const singletoneTypes = [
  'NotificationSettings',
  'XeroState',
  'NetsuiteState',
  'QuickBooksState',
  'FinaloopState',
  'BusinessFeaturesType',
]

export const getApolloCache = () =>
  new InMemoryCache({
    typePolicies: {
      Mergeable: {
        keyFields: false,
        merge: true,
      },
      NonMergeable: {
        keyFields: false,
        merge: false,
      },
      Singletone: {
        keyFields: [],
      },
      Shipment: {
        fields: {
          shippingVendors: {
            merge: mergeAcceptIncoming,
          },
          lineItems: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      WorkOrder: {
        fields: {
          lineItems: {
            merge: mergeAcceptIncoming,
          },
          linkedDocuments: {
            merge: mergeAcceptIncoming,
          },
          additionalExpenses: {
            merge: mergeAcceptIncoming,
          },
          shipments: {
            merge: mergeAcceptIncoming,
          },
          milestones: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      PurchaseOrder: {
        fields: {
          lineItems: {
            merge: mergeAcceptIncoming,
          },
          shippingVendors: {
            merge: mergeAcceptIncoming,
          },
          linkedDocuments: {
            merge: mergeAcceptIncoming,
          },
          additionalExpenses: {
            merge: mergeAcceptIncoming,
          },
          shipments: {
            merge: mergeAcceptIncoming,
          },
          milestones: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      Receipt: {
        fields: {
          shippingVendors: {
            merge: mergeAcceptIncoming,
          },
          lineItems: {
            merge: mergeAcceptIncoming,
          },
          linkedDocuments: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      InventoryItemDemandForecast: {
        keyFields: ['period', 'catalogItemVariation', ['id']],
        fields: {
          channelForecast: {
            merge: mergeAcceptIncoming,
          },
          manualForecast: {
            merge: mergeAcceptIncoming,
          },
          totalForecast: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      InvoiceLineItem: {
        fields: {
          linkedPurchaseOrderExpenses: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      InventoryTransfer: {
        fields: {
          lineItems: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      CatalogItem: {
        fields: {
          barcodes: {
            merge: mergeAcceptIncoming,
          },
          properties: {
            merge: mergeAcceptIncoming,
          },
          variations: {
            merge: mergeAcceptIncoming,
          },
          salesDetails: {
            merge: mergeAcceptIncoming,
          },
          purchaseDetails: {
            read: filterEmptyPurchaseDetails,
            merge: mergeAcceptIncoming,
          },
        },
      },
      DocumentsMatchingData: {
        fields: {
          matchedLines: {
            merge: mergeAcceptIncoming,
          },
          unmatchedBillLines: {
            merge: mergeAcceptIncoming,
          },
          unmatchedPurchaseOrderLines: {
            merge: mergeAcceptIncoming,
          },
          unmatchedReceiptLines: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      CatalogItemVariation: {
        fields: {
          barcodes: {
            merge: mergeAcceptIncoming,
          },
          propertyValues: {
            merge: mergeAcceptIncoming,
          },
          salesDetails: {
            merge: mergeAcceptIncoming,
          },
          purchaseDetails: {
            read: filterEmptyPurchaseDetails,
            merge: mergeAcceptIncoming,
          },
        },
      },
      CatalogItemProperty: {
        fields: {
          values: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      OutboundOrder: {
        fields: {
          timelineEvents: {
            merge: mergeAcceptIncoming,
          },
          lineItems: {
            merge: mergeAcceptIncoming,
          },
          orderDate: {
            read: readManualOrderDate,
          },
          targetFulfillmentDate: {
            read: readManualOrderDate,
          },
        },
      },
      Invoice: {
        fields: {
          lineItems: {
            merge: mergeAcceptIncoming,
          },
          linkedDocuments: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      ReceivableInvoice: {
        fields: {
          lineItems: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      SettleInboxMessage: {
        fields: {
          body: {
            merge: mergePreservePopulatedString,
          },
        },
      },
      LocalBusiness: {
        fields: {
          vendorPaymentTerms: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      Journey: {
        keyFields: ['type'],
        fields: {
          steps: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      Query: {
        fields: {
          ...replaceableArrays,
          userRoles: {
            merge: mergeAcceptIncoming,
          },
          rules: {
            merge: mergeAcceptIncoming,
          },
          billingMethods: {
            merge: mergeAcceptIncoming,
          },
          journeys: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      Dashboard: {
        fields: {
          actionableItems: relayStylePagination(),
        },
      },
      ExternalItem: {
        fields: {
          children: {
            merge: mergeAcceptIncoming,
          },
        },
      },
      ShopifyProduct: {
        keyFields: ['externalId'],
      },
      PublicInvoice: {
        keyFields: ['uuid'],
      },
      BillingPlanFeatures: {
        keyFields: ['feature', 'supportedByPlan', 'usageData'],
      },
      JourneyStep: {
        keyFields: ['type', 'relatedObjectData'],
      },
      CodeTypeV2: {
        keyFields: ['referralCode'],
      },
      ApAgingBucketSummary: {
        keyFields: ['dayRange'],
      },
    },
    possibleTypes: {
      // Types that don't have id or uniq filed, that should be merged into one object.
      // In other words, only one instance of this type can exist in application
      Mergeable: autoMergeTypes,
      // Types that don't have id or uniq filed, that couldn't be merged into one object.
      // In other words, there are many instances of such types that are not connected to each other.
      // Those types won't be normalized by Apollo cache
      NonMergeable: ['QuickBooksDictionary', 'InternationalWireFieldsDataFieldValueType'],
      SyncObject: ['Invoice', 'VendorCredit', 'CatalogItem', 'PurchaseOrder', 'Payment'],
      LinkedDocument: ['Invoice', 'PurchaseOrder', 'Receipt', 'DocumentReference'],
      OcrDocument: ['Invoice', 'PurchaseOrder'],
      Singletone: singletoneTypes,
      Integration: ['NetsuiteState', 'QuickBooksState', 'XeroState'],
      AuditTrailEvents: ['Comment', 'TimelineEventV2'],
      MfaChallenge: ['PhoneFactorChallenge', 'TotpChallenge'],
    },
  })
